import { useState, useMemo } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import type { UserMetrics } from '../../../api/generated';

type SortKey = 'display_name' | 'conversations_created' | 'analyses_count' | 'avg_score' | 'messages_sent';

interface UserLeaderboardProps {
  users: UserMetrics[];
}

export function UserLeaderboard({ users }: UserLeaderboardProps) {
  const [sortKey, setSortKey] = useState<SortKey>('avg_score');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const sorted = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = a[sortKey] ?? -1;
      const bVal = b[sortKey] ?? -1;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [users, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  if (users.length === 0) {
    return null;
  }

  const columns: { key: SortKey; label: string; align?: 'right' | 'left' }[] = [
    { key: 'display_name', label: 'Name', align: 'left' },
    { key: 'conversations_created', label: 'Conversations', align: 'right' },
    { key: 'analyses_count', label: 'Analyses', align: 'right' },
    { key: 'avg_score', label: 'Avg Score', align: 'right' },
    { key: 'messages_sent', label: 'Messages', align: 'right' },
  ];

  return (
    <TableContainer>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((col) => (
              <TableCell key={col.key} align={col.align}>
                <TableSortLabel
                  active={sortKey === col.key}
                  direction={sortKey === col.key ? sortDir : 'desc'}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((user) => (
            <TableRow key={user.user_id}>
              <TableCell>{user.display_name || user.email}</TableCell>
              <TableCell align="right">{user.conversations_created}</TableCell>
              <TableCell align="right">{user.analyses_count}</TableCell>
              <TableCell align="right">
                {user.avg_score != null ? user.avg_score.toFixed(0) : '-'}
              </TableCell>
              <TableCell align="right">{user.messages_sent}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
